import { Injectable } from '@angular/core';

export interface SystemConfig {
  name: string;
  url: string;
  permission: string;
}

export interface AppConfig {
  apiUrl: string;
  environmentName: string;
  recaptchaSiteKey: string;
  /** Path relativo al apiUrl. Default: `/guest/token`. */
  guestTokenEndpoint?: string;
  systems: SystemConfig[];
}

@Injectable({ providedIn: 'root' })
export class ConfigService {
  private config!: AppConfig;

  constructor() {
    // Leemos la configuración que fue inyectada en window por main.ts
    const windowConfig = (window as any)['__APP_CONFIG__'] as AppConfig;

    if (windowConfig) {
      this.config = windowConfig;
    } else {
      // Fallback por si acaso main.ts falló (no debería pasar)
      console.warn(
        'ConfigService: No se encontró __APP_CONFIG__ en window, usando fallback.',
      );
      this.config = this.getDefaultConfig();
    }
  }

  /**
   * Carga la configuración.
   * En esta arquitectura, el fetch lo hace main.ts.
   * Este método existe por si alguna librería externa lo pide, pero devolvemos una promesa resuelta.
   */
  loadConfig(): Promise<AppConfig> {
    return Promise.resolve(this.config);
  }

  private getDefaultConfig(): AppConfig {
    return {
      apiUrl: 'http://localhost:8080',
      environmentName: 'local-fallback',
      recaptchaSiteKey: '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI', // test key
      systems: [
        {
          name: 'Admin Central',
          url: '/app/dashboard',
          permission: 'system:admin',
        },
      ],
    };
  }

  get apiUrl(): string {
    return this.config?.apiUrl || '';
  }

  get environmentName(): string {
    return this.config?.environmentName || 'local';
  }

  get systems(): SystemConfig[] {
    return this.config?.systems || [];
  }

  get recaptchaSiteKey(): string {
    return this.config?.recaptchaSiteKey || '';
  }

  /** Endpoint guest token relativo a `apiUrl` (ej. `/guest/token`). */
  get guestTokenEndpoint(): string {
    const raw = this.config?.guestTokenEndpoint?.trim();
    return raw || '/guest/token';
  }

  getAuthorizedSystems(userPermissions: string[]): SystemConfig[] {
    if (!this.config?.systems) return [];
    return this.config.systems.filter((sys) =>
      userPermissions.includes(sys.permission),
    );
  }
}
